"use client";

import { useState } from "react";
import { Icon } from "@iconify/react";
import { uiIcons } from "@/config/icons";
import { v4 as uuidv4, v1 as uuidv1 } from "uuid";
import { usePathname } from "next/navigation";
import { getLocaleFromPathname } from "@/lib/i18n";
import { getDevToolCopy } from "@/lib/dev-tool-copy";
import { getToolUiCopy } from "@/lib/tool-ui-copy";
import { tools } from "@/config/tools";
import { getLocalizedTool } from "@/lib/tool-localization";

export function UuidGenerator() {
  const [uuids, setUuids] = useState<string[]>([]);
  const [version, setVersion] = useState<"v4" | "v1">("v4");
  const [count, setCount] = useState(1);
  const pathname = usePathname() || "/";
  const locale = getLocaleFromPathname(pathname);
  const dev = getDevToolCopy(locale);
  const ui = getToolUiCopy(locale);
  const localizedTool = getLocalizedTool(
    tools.find((item) => item.id === "uuid-generator") ?? tools[0],
    locale
  );

  const generateUUIDs = () => {
    const newUuids: string[] = [];
    for (let i = 0; i < count; i++) {
      newUuids.push(version === "v4" ? uuidv4() : uuidv1());
    }
    setUuids(newUuids);
  };

  const handleCopy = async (uuid: string) => {
    await navigator.clipboard.writeText(uuid);
  };

  const handleCopyAll = async () => {
    await navigator.clipboard.writeText(uuids.join("\n"));
  };

  return (
    <div className="space-y-8">
      <div className="rounded-[20px] border border-positivus-dark/10 bg-positivus-green/10 p-6">
        <div className="flex items-start gap-4">
          <div className="hidden md:block rounded-full bg-positivus-green/20 p-2">
            <Icon icon="mdi:id-card" className="h-6 w-6 text-positivus-dark" />
          </div>
          <div className="text-sm text-positivus-dark w-full">
            <p className="font-medium text-lg mb-2">{localizedTool.name}</p>
            <p className="leading-relaxed">{localizedTool.description}</p>
          </div>
        </div>
      </div>

      <div className="space-y-8">
         <div className="grid gap-8 sm:grid-cols-2">
          <div className="space-y-3">
             <label className="text-lg font-medium text-positivus-dark">{dev.uuidVersion}</label>
             <div className="relative">
                <select
                value={version}
                onChange={(e) => setVersion(e.target.value as "v4" | "v1")}
                className="w-full appearance-none rounded-[15px] border-2 border-positivus-dark/20 bg-white px-5 py-4 text-positivus-dark focus:border-positivus-dark focus:outline-none focus:ring-0 transition-all font-bold cursor-pointer text-lg"
                >
                <option value="v4">{dev.version4} (Random)</option>
                <option value="v1">{dev.version1} (Timestamp)</option>
                </select>
                <Icon icon="mdi:chevron-down" className="absolute right-4 top-1/2 -translate-y-1/2 h-6 w-6 text-positivus-dark pointer-events-none" />
             </div>
          </div>

          <div className="space-y-3">
             <label className="text-lg font-medium text-positivus-dark">{dev.quantity}</label>
             <div className="relative">
                 <input
                    type="number"
                    min="1"
                    max="100"
                    value={count}
                    onChange={(e) => setCount(parseInt(e.target.value) || 1)}
                    className="w-full rounded-[15px] border-2 border-positivus-dark/20 bg-white px-5 py-4 text-positivus-dark focus:border-positivus-dark focus:outline-none focus:ring-0 transition-all font-bold text-lg"
                 />
             </div>
          </div>
        </div>

        <div className="rounded-[15px] border-2 border-blue-500/20 bg-blue-50 px-5 py-4 flex items-start gap-3">
            <Icon icon="mdi:information-outline" className="h-6 w-6 text-blue-600 mt-0.5 shrink-0" />
            <div className="text-sm text-blue-900">
                <p className="font-bold text-base mb-1">{dev.aboutUuidVersions}</p>
                <ul className="space-y-1 list-disc list-inside ml-2">
                <li><strong>{dev.version4}:</strong> {dev.version4Body}</li>
                <li><strong>{dev.version1}:</strong> {dev.version1Body}</li>
                </ul>
            </div>
        </div>

        <button
            onClick={generateUUIDs}
            className="w-full rounded-[20px] bg-positivus-dark px-6 py-5 text-xl font-bold text-white hover:bg-positivus-green hover:text-positivus-dark transition-all shadow-[0_5px_0_0_#191A23] hover:shadow-none hover:translate-y-[2px]"
        >
            <div className="flex items-center justify-center gap-3">
                <Icon icon="mdi:refresh" className="h-6 w-6" />
                <span>{count > 1 ? dev.generateUuids : dev.generateUuid}</span>
            </div>
        </button>
      </div>

      {uuids.length > 0 && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-2xl font-bold text-positivus-dark">{dev.results} ({uuids.length})</h3>
            {uuids.length > 1 && (
              <button
                onClick={handleCopyAll}
                className="flex items-center gap-2 rounded-[10px] bg-positivus-green px-4 py-2 font-bold text-positivus-dark border-2 border-positivus-dark hover:bg-positivus-dark hover:text-white transition-all shadow-[0_3px_0_0_#191A23] hover:shadow-none hover:translate-y-[1px]"
              >
                <Icon icon={uiIcons.copy} className="h-4 w-4" />
                {dev.copyAll}
              </button>
            )}
          </div>

          <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
            {uuids.map((uuid, index) => (
              <div
                key={index}
                className="flex items-center justify-between rounded-[15px] border-2 border-positivus-dark/10 bg-white p-4 hover:border-positivus-dark hover:shadow-[0_4px_0_0_#191A23] transition-all group"
              >
                <code className="text-lg font-mono text-positivus-dark">{uuid}</code>
                <button
                  onClick={() => handleCopy(uuid)}
                  className="opacity-0 group-hover:opacity-100 flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-bold bg-gray-100 text-positivus-dark hover:bg-positivus-green transition-all"
                >
                   <Icon icon={uiIcons.copy} className="h-4 w-4" />
                   {ui.copy}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
